// Auto mode's summary on the planning pages: when it last ran, the draft POs it made or changed (links), what it
// skipped and why, how many items are set to auto-reorder, and "Run now". It never sends anything.
import { Bot, Play } from "lucide-react";
import { useState } from "react";
import type { Plan } from "../../../../shared/planning.ts";
import { ago, personName } from "../../lib/format.ts";
import { useLastAutoRun } from "../../lib/make.ts";
import { can, mutateWithResult } from "../../lib/mutate.ts";
import { useRows } from "../../lib/sync.ts";
import { Button, cx } from "../../ui/controls.tsx";
import { Popover } from "../../ui/Popover.tsx";
import { PaneLink } from "../../ui/SidePane.tsx";
import { toast } from "../../ui/toast.tsx";
import { poPath } from "../po/lib.ts";

type RunPo = {
  po_id: string;
  number: string;
  vendor: string;
  ship_to: string;
  kind: "purchase" | "make";
  created: boolean;
  added: number;
  updated: number;
  removed: number;
  kept: number;
  lines: number;
  deleted?: boolean;
};
type RunSummary = { pos?: RunPo[]; skipped?: { sku: string; reason: string }[] };

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

function poText(p: RunPo) {
  if (p.deleted) return "removed: nothing is due";
  if (p.created) return `new, ${plural(p.lines, "line")}`;
  const parts = [p.added && `${p.added} added`, p.updated && `${p.updated} changed`, p.removed && `${p.removed} removed`, p.kept && `${p.kept} left as edited`];
  return parts.filter(Boolean).join(", ") || "up to date";
}

export function AutoRunButton({ plan }: { plan: Plan }) {
  const run = useLastAutoRun();
  const users = useRows("app_user");
  const [busy, setBusy] = useState(false);
  const autoItems = plan.results.filter((r) => r.policy === "auto").length;
  const summary = (run?.summary ?? {}) as RunSummary;
  const changed = (run?.pos_created ?? 0) + (run?.pos_updated ?? 0);
  const runNow = async () => {
    setBusy(true);
    const { ok, result } = await mutateWithResult("plan.auto_reorder", {});
    setBusy(false);
    if (!ok) return;
    const r = result as { pos_created: number; pos_updated: number; pos_removed: number; due: number };
    toast.ok(
      r.due || r.pos_removed
        ? `Auto mode: ${plural(r.pos_created, "new draft")}, ${plural(r.pos_updated, "draft")} changed${r.pos_removed ? `, ${r.pos_removed} removed` : ""}`
        : "Auto mode: nothing set to auto-reorder is due",
    );
  };
  const by = run?.run_by ? personName(users, run.run_by) : "the nightly run";
  return (
    <Popover
      width={400}
      button={(toggle, open) => (
        <Button
          variant="ghost"
          icon={<Bot className="size-4" />}
          onClick={toggle}
          className={cx(open && "bg-hover text-text")}
          title="Auto mode: draft POs for items set to auto-reorder"
        >
          <span className="hidden xl:inline">{run ? `Auto ${ago(run.started_at)}${changed ? ` · ${changed}` : ""}` : "Auto mode"}</span>
        </Button>
      )}
    >
      {() => (
        <div className="text-[12px]">
          <div className="mb-1 flex items-center gap-2">
            <span className="font-medium">Auto mode</span>
            <span className="text-muted">{plural(autoItems, "item")} set to auto-reorder</span>
            {can("plan.auto_reorder") && (
              <Button size="sm" className="ml-auto" icon={<Play className="size-3.5" />} disabled={busy} onClick={runNow}>
                Run now
              </Button>
            )}
          </div>
          <p className="mb-2 text-muted">
            Every night after the forecast, items set to "Auto-reorder" in their planning settings get draft POs (builds as make POs to their CM). Nothing is
            sent: review and send them. Later runs keep auto mode's own drafts up to date while they're drafts and leave lines you changed alone.
          </p>
          {!run ? (
            <div className="text-faint">It hasn't run yet.</div>
          ) : (
            <>
              <div className="mb-1 text-muted">
                Last run {ago(run.started_at)} by {by}: {plural(run.pos_created, "new draft")}, {plural(run.pos_updated, "draft")} changed
                {run.pos_removed ? `, ${run.pos_removed} removed` : ""}.
              </div>
              {(summary.pos ?? []).length > 0 && (
                <ul className="mb-1 space-y-0.5">
                  {(summary.pos ?? []).map((p) => (
                    <li key={p.po_id} className="flex gap-1.5">
                      {p.deleted ? (
                        <span className="font-mono text-faint line-through">{p.number}</span>
                      ) : (
                        <PaneLink kind="po" id={p.po_id} to={poPath(p.po_id)} className="font-mono text-accent-text hover:underline">
                          {p.number || "Draft"}
                        </PaneLink>
                      )}
                      <span className="min-w-0 truncate">
                        {p.vendor}
                        {p.kind === "make" ? " (build)" : ""}
                        {p.ship_to ? ` → ${p.ship_to}` : ""}
                      </span>
                      <span className="ml-auto shrink-0 text-muted">{poText(p)}</span>
                    </li>
                  ))}
                </ul>
              )}
              {(summary.skipped ?? []).length > 0 && (
                <ul className="mt-1 rounded-md bg-warn-soft px-2 py-1 text-warn">
                  {(summary.skipped ?? []).map((s) => (
                    <li key={`${s.sku}:${s.reason}`}>
                      {s.sku}: {s.reason}
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
      )}
    </Popover>
  );
}
